import type { PostOfficeContextConfig as Context } from '@atlassian/post-office-context/types';

import type { Fetcher } from '@atlassian/post-office-placement-data/types';

import { PlacementFetchingContext } from '@atlassian/post-office-placement-data/placement-fetcher-provider';

import { createPlacementComponent } from '@post-office/placement-common/utils/create-placement-component';

import { createUsePlacement, useInitialData } from '@post-office/placement-contexts/hooks';

import { type ComponentType, useContext } from 'react';

import { withInAppExampleIntlProvider } from '../__generated__/intl-provider';

import type { InAppExampleAdditionalData, InAppExampleInitialData } from '../api/types';

import { type InAppExamplePlacement } from '../contracts/InAppExamplePlacement';

import { type OnPerformanceTrackingEvent } from '@post-office/placement-contexts/create-placement-component/hooks/usePerformanceTracking/types';

import {
	type ContextWithRoute,
	type ErrorBoundaryAdditionalAnalyticsFacade,
	type FallbackRenderFacade,
} from '@post-office/placement-contexts/create-placement-component/types';

import { type ErrorBoundaryPropsWithRender } from 'react-error-boundary';

const PLACEMENT_ID = 'in-app-example';

export const InAppExamplePlacementComponent: ComponentType<
	Partial<ErrorBoundaryPropsWithRender> & {
		context: ContextWithRoute;
		initialData: Promise<Response | undefined> | undefined;
		fetch?: Fetcher;
		onEvent?: OnPerformanceTrackingEvent;
	} & InAppExamplePlacement &
		ErrorBoundaryAdditionalAnalyticsFacade &
		FallbackRenderFacade & {
			messages?: {
				[x: string]: string;
			};
		}
> = withInAppExampleIntlProvider(
	createPlacementComponent<InAppExamplePlacement>(
		PLACEMENT_ID,
		({ context, initialData, zooName }) => {
			const fetcher = useContext(PlacementFetchingContext);
			const useInAppExampleData = createUsePlacement(PLACEMENT_ID, fetcher);

			const initialDataState = useInitialData<InAppExampleInitialData>(initialData);

			if (initialDataState.isLoading) {
				return <p>Loading</p>;
			}

			const additionalDataState = useInAppExampleData<InAppExampleAdditionalData, Context>(
				'/additional-data',
				context,
			);

			if (additionalDataState.isLoading || additionalDataState.error) {
				return <p>Loading or Error</p>;
			}

			return (
				<p>
					`This ${zooName} has ${additionalDataState.data.animals.length} animals.`
				</p>
			);
		},
		{
			/**
			 * If your placement contains Marketing or Engagement messages and is NOT disruptive, you should disable the choreographer.
			 * Do so by changing the following below to `false` OR by importing and calling `useDisableChoreographer()`.
			 */
			choreographer: {
				isDefaultEnabled: true, // change this to 'false' if disabling the Choreographer
			},
		},
	),
);

export default InAppExamplePlacementComponent;
